import * as React from 'react';
import { LocalizeContextProps, withLocalize } from 'react-localize-redux';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import Select, { ValueType } from 'react-select';
import { Button, Form, Input } from 'reactstrap';
import { Dispatch } from 'redux';
import urlencode from 'urlencode';
import { Privilege, Workspace } from '../../clients/aasServerClient.generated';
import { applicationOptions } from '../../stores/applications/selectors';
import { getAssignablePrivileges, hideGroups } from '../../stores/auth/selectors';
import { groupOptions } from '../../stores/groups/selectors';
import { isLoading } from '../../stores/navigation/selectors';
import { RootState } from '../../stores/store';
import {
    clearSingleWorkspace, fetchSingleWorkspace,
    saveSingleWorkspace
} from '../../stores/workspaces/actionCreators';
import { getSingleWorkspace } from '../../stores/workspaces/selectors';
import ApplicationsSelect from '../applications/ApplicationSelect';
import FormInput from '../common/FormInput';
import SideModal from '../common/modals/SideModal';
import GroupsSelect from '../groups/GroupSelect';
import { LoadingButton } from '../navigation/LoadingButton';
import LoadingScreen from '../navigation/LoadingScreen';
import WorkspaceProperties from './WorkspaceProperties';
import WorkspaceSelect from './WorkspaceSelect';

interface Props extends LocalizeContextProps, RouteComponentProps<{ id: string }> {
    isLoading: boolean;
    workspace: {
        loaded: boolean;
        saved: boolean;
        workspace?: Partial<Workspace>;
    };

    groups: Array<{ label: string, value: string }>;
    hideGroups?: boolean;

    fetchSingleWorkspace: (id: string) => void;
    clearSingleWorkspace: () => void;
    saveSingleWorkspace: (
        workspace: Partial<Workspace>,
        parentId?: string,
        groups?: Array<{ id: string, value: boolean }>,
    ) => void;
}

interface State {
    workspace?: Partial<Workspace>;
    groupSelection?: ValueType<{ label: string, value: string }>;
    parentSelection?: { label: string, value: string };
    saved: boolean;
}

class EditWorkspacePage extends React.Component<Props, State> {

    public constructor(props: Props) { 
        super(props);
        this.state = {
            workspace: undefined,
            saved: true,
        };
        const id = this.props.match.params.id; 
        this.props.clearSingleWorkspace();
        this.props.fetchSingleWorkspace(id);
    }

    public componentDidUpdate(prevProps: Props) {
        if (!prevProps.workspace.loaded && this.props.workspace.loaded) {
            this.setState({
                workspace: this.props.workspace.workspace,
                saved: true
            });
        }
        if (!prevProps.workspace.saved && this.props.workspace.saved) {
            this.closePage();
        }
    }
    public modalRef = React.createRef<HTMLDivElement>();
    public render() {
        const t = this.props.translate;
        return (
            <SideModal
                isOpen={true}
                toggle={this.closePage}
                headerText={t('workspaces.edit.header.edit') as string}
                footer={this.renderFooter()}
                objectRef={this.modalRef}
            >
                {this.props.workspace.loaded && this.props.workspace.workspace ? (
                    <Form>
                        <h3 className="mb-4 mt-2">{t('workspaces.edit.section.details')}</h3>
                        <WorkspaceProperties
                            type="edit"
                            workspace={this.props.workspace.workspace}
                            saved={this.state.saved}
                            updateWorkspace={this.updateWorkspace}
                        />
                        {this.props.workspace.workspace!.parent ? (
                            <React.Fragment>
                                <hr className="fill-modal" />
                                <h3 className="mb-4 mt-4">{t('workspaces.edit.section.parent')}</h3>
                                <WorkspaceSelect
                                    selected={this.props.workspace.workspace.parent.id}
                                    updateSelection={this.updateParentSelection}
                                    selection={this.state.parentSelection}
                                />
                            </React.Fragment>
                        ) : null}
                        {this.props.hideGroups === false && this.props.workspace.workspace!.groups ? (
                            <React.Fragment>
                                <hr className="fill-modal" />
                                <h3 className="mb-4 mt-4">{t('workspaces.edit.section.groups')}</h3>
                                <GroupsSelect
                                    selected={this.props.workspace.workspace.groups.map((group) => group.id)}
                                    updateSelection={this.updateGroupSelection}
                                    selection={this.state.groupSelection}
                                    filterPerWorkspace= {false}
                                />
                            </React.Fragment>
                        ) : null}

                    </Form>
                ) : (
                        <LoadingScreen text={t('loading.loading') as string} />
                    )}
            </SideModal>
        );
    }

    private updateWorkspace = (workspace: Partial<Workspace>) => {
        this.setState({
            workspace,
            saved: false
        });
    }

    private updateGroupSelection = (selection: ValueType<{ label: string, value: string }>) => {
        this.setState({ groupSelection: selection });
    }

    private updateParentSelection = (selection: ValueType<{ label: string, value: string }> |
    { label: string, value: string }) => {
        this.setState({ parentSelection: selection as { label: string, value: string } });
    }

    private closePage = () => {
        this.props.history.push('/workspaces');
    }

    private renderFooter = (): React.ReactNode => {
        const t = this.props.translate;
        return (
            <React.Fragment>
                <LoadingButton
                    loading={this.props.isLoading}
                    color="success"
                    onClick={this.saveWorkspace}
                    style={{ width: '200px' }}
                >
                    {t('workspaces.edit.button.save')}
                </LoadingButton>
            </React.Fragment>
        );
    }

    private saveWorkspace = () => {
        const workspace = this.state.workspace;
        let groups: Array<{ id: string; value: boolean }> | undefined;
        if (this.state.groupSelection) {
            groups = this.props.groups.map((group) => ({
                id: group.value,
                // tslint:disable-next-line:no-any
                value: (this.state.groupSelection as any[]).find((x) => x.value === group.value) !== undefined,
            }));
        }

        const parentId = this.state.parentSelection?.value;
        this.props.saveSingleWorkspace(workspace!, parentId, groups);
    }
}

const mapStateToProps = (state: RootState) => ({
    groups: groupOptions(state),
    hideGroups: hideGroups(state),
    isLoading: isLoading(state),
    workspace: getSingleWorkspace(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    clearSingleWorkspace: () => dispatch(clearSingleWorkspace()),
    fetchSingleWorkspace: (id: string) => dispatch(fetchSingleWorkspace(id)),
    saveSingleWorkspace: (
        workspace: Partial<Workspace>,
        parentId?: string,
        groups?: Array<{ id: string, value: boolean }>,
    ) => dispatch(saveSingleWorkspace(workspace, parentId, groups))
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withLocalize(EditWorkspacePage)));